/* eslint-disable */

import axios from 'axios';
import Vue from 'vue';
import { normalizeMinimumNoticeHours } from '@/utils/schedulingWindow';

// Note: axios.defaults.baseURL is already set globally in main.js
// Do not override it here as it affects all axios calls

export default {
  namespaced: true,

  state: {
    // Current active data (for the selected location)
    settings: {},
    procedureCodes: [],
    providers: [],
    operatories: [],
    locations: [],
    blockoutTypes: [],
    confirmationStatuses: [],

    // Server-computed booking window, a SIBLING of `settings` in the read response
    // ({ mode, start, end, months, todayYmd, empty, warnings, timezone }). It is NEVER
    // merged into `settings` — saves spread the whole settings object back, so a computed
    // value living inside it would get persisted. null on an older backend.
    effectiveSchedulingWindow: null,

    // Multi-location support
    paidLocations: [], // Locations with ONLINE_SCHEDULING_PAID_FOR = TRUE for this customerID
    settingsCache: {}, // Cache of settings per SiteID: { [siteId]: { settings, providers, operatories, blockoutTypes, loaded } }
    locationStatus: {}, // Setup status per location: { [siteId]: { configured: true/false, appointmentTypeCount: 0 } }

    // UI state
    showLocationSelection: false,
    loading: false,
    saving: false,
    error: null,
    loaded: false
  },

  getters: {
    // Get setup status for a specific location
    getLocationStatus: (state) => (siteId) => {
      return state.locationStatus[siteId] || { configured: false, appointmentTypeCount: 0 };
    },

    // Check if all locations are configured
    allLocationsConfigured: (state) => {
      if (state.paidLocations.length === 0) return true;
      return state.paidLocations.every(loc => {
        const status = state.locationStatus[loc.siteId];
        return status && status.configured;
      });
    },

    // Get cached settings for a location
    getCachedSettings: (state) => (siteId) => {
      return state.settingsCache[siteId] || null;
    }
  },

  mutations: {
    SET_LOADING(state, val) { state.loading = val; },
    SET_SAVING(state, val) { state.saving = val; },
    SET_ERROR(state, val) { state.error = val; },
    SET_LOADED(state, val) { state.loaded = val; },

    SET_ALL_DATA(state, data) {
      state.settings = data.settings || {};
      state.procedureCodes = data.procedureCodes || [];
      state.providers = data.providers || [];
      state.operatories = data.operatories || [];
      state.locations = data.locations || [];
      state.blockoutTypes = data.blockoutTypes || [];
      state.confirmationStatuses = data.confirmationStatuses || [];
      state.effectiveSchedulingWindow = data.effectiveSchedulingWindow || null;
      state.showLocationSelection = (data.locations && data.locations.length > 1) || false;
    },

    SET_SETTINGS(state, settings) {
      state.settings = settings;
    },

    SET_PAID_LOCATIONS(state, locations) {
      state.paidLocations = locations || [];
    },

    // Cache settings for a specific location
    CACHE_LOCATION_DATA(state, { siteId, data }) {
      Vue.set(state.settingsCache, siteId, {
        settings: data.settings || {},
        providers: data.providers || [],
        operatories: data.operatories || [],
        blockoutTypes: data.blockoutTypes || [],
        confirmationStatuses: data.confirmationStatuses || [],
        procedureCodes: data.procedureCodes || [],
        effectiveSchedulingWindow: data.effectiveSchedulingWindow || null,
        loaded: true,
        loadedAt: Date.now()
      });
    },

    // Update cached settings for a location
    UPDATE_CACHED_SETTINGS(state, { siteId, settings }) {
      if (state.settingsCache[siteId]) {
        Vue.set(state.settingsCache[siteId], 'settings', settings);
      }
    },

    // Set location setup status
    SET_LOCATION_STATUS(state, { siteId, status }) {
      Vue.set(state.locationStatus, siteId, status);
    },

    // Clear all caches (useful when switching customers)
    CLEAR_CACHES(state) {
      state.settingsCache = {};
      state.locationStatus = {};
    }
  },

  actions: {
    /**
     * Load settings for a specific location
     * Caches the result for faster switching between locations
     */
    async loadSettingsForLocation({ commit, state }, { siteID, forceReload = false }) {
      // Check cache first (unless force reload)
      const cached = state.settingsCache[siteID];
      if (cached && cached.loaded && !forceReload) {
        // Use cached data - update current state
        commit('SET_ALL_DATA', {
          settings: cached.settings,
          providers: cached.providers,
          operatories: cached.operatories,
          blockoutTypes: cached.blockoutTypes,
          confirmationStatuses: cached.confirmationStatuses,
          procedureCodes: cached.procedureCodes,
          effectiveSchedulingWindow: cached.effectiveSchedulingWindow || null,
          locations: state.locations
        });
        commit('SET_LOADED', true);
        return cached;
      }

      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        console.log('[onlineAppointments] Loading settings for siteID:', siteID);

        // Use dedicated online-scheduling-service endpoint (follows huddle.js pattern)
        let response = await axios.post('/get-online-scheduling-settings', {
          siteID: siteID
        }, { crossdomain: true });

        if (response.data && response.data.success) {
          const data = response.data.data;

          // Cache the data
          commit('CACHE_LOCATION_DATA', { siteId: siteID, data });

          // Update current state
          commit('SET_ALL_DATA', data);
          commit('SET_LOADED', true);

          // Update location status
          const appointmentTypes = data.settings?.appointmentTypes || [];
          const hasEmail = !!(data.settings?.notificationEmail);
          commit('SET_LOCATION_STATUS', {
            siteId: siteID,
            status: {
              configured: appointmentTypes.length > 0 && hasEmail,
              appointmentTypeCount: appointmentTypes.length,
              hasEmail: hasEmail
            }
          });

          return data;
        } else {
          throw new Error(response.data?.message || 'Failed to load settings');
        }
      } catch (err) {
        console.error('[onlineAppointments] Error loading settings for siteID:', siteID, err);
        if (err.response) {
          console.error('[onlineAppointments] Error response data:', err.response.data);
          console.error('[onlineAppointments] Error response status:', err.response.status);
        }
        commit('SET_ERROR', err.message || 'Failed to load settings');
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Original loadSettings - now calls loadSettingsForLocation
     */
    async loadSettings({ dispatch }, { siteID }) {
      return dispatch('loadSettingsForLocation', { siteID, forceReload: false });
    },

    /**
     * Read ONE site's online-scheduling settings WITHOUT touching this module's state: no SET_ALL_DATA, no cache write,
     * no loading / error flag. For readers outside the Online Appointments page (Appointment Reminders → Settings, the
     * "Online scheduling link" picker, which needs the site's appointment types): loadSettingsForLocation commits
     * SET_ALL_DATA, which would swap what the Online Appointments page is showing for this site's data. Same endpoint and
     * same response parsing as loadSettingsForLocation. Resolves the `settings` object ({ appointmentTypes: [...], ... });
     * throws when the read fails - the caller decides what that means.
     */
    async readSettingsQuietly(_ctx, { siteID }) {
      const response = await axios.post('/get-online-scheduling-settings', {
        siteID: siteID
      }, { crossdomain: true });

      if (response.data && response.data.success) {
        const data = response.data.data || {};
        return data.settings || {};
      }
      throw new Error(response.data?.message || 'Failed to load settings');
    },

    /**
     * Save settings for a specific location
     * Uses dedicated online-scheduling-service endpoint (follows huddle.js pattern)
     */
    async saveSettings({ commit, state }, { siteID, settings, skipGlobalState = false }) {
      commit('SET_SAVING', true);
      commit('SET_ERROR', null);
      try {
        console.log('[onlineAppointments] Saving settings for siteID:', siteID);

        let response = await axios.post('/save-online-scheduling-settings', {
          siteID: siteID,
          settings: settings
        }, { crossdomain: true });

        if (response.data && response.data.success) {
          // Update current state (skip for propagation saves to avoid overwriting the active view)
          if (!skipGlobalState) {
            commit('SET_SETTINGS', settings);
          }

          // Update cache
          commit('UPDATE_CACHED_SETTINGS', { siteId: siteID, settings });

          // Update location status
          const appointmentTypes = settings?.appointmentTypes || [];
          const hasEmail = !!(settings?.notificationEmail);
          commit('SET_LOCATION_STATUS', {
            siteId: siteID,
            status: {
              configured: appointmentTypes.length > 0 && hasEmail,
              appointmentTypeCount: appointmentTypes.length,
              hasEmail: hasEmail
            }
          });
        } else {
          throw new Error(response.data?.message || 'Failed to save settings');
        }
        return response;
      } catch (err) {
        console.log('Error saving online appointment settings:', err);
        commit('SET_ERROR', err.message || 'Failed to save settings');
        throw err;
      } finally {
        commit('SET_SAVING', false);
      }
    },

    /**
     * Load all locations (SiteIDs) for this customerID that have ONLINE_SCHEDULING_PAID_FOR = TRUE
     * Uses dedicated backend endpoint (follows huddle.js pattern)
     */
    async loadPaidLocations({ commit, dispatch, rootState }, { customerID, siteID }) {
      if (!customerID) {
        console.warn('[onlineAppointments] loadPaidLocations called without customerID');
        commit('SET_PAID_LOCATIONS', []);
        return;
      }

      try {
        console.log('[onlineAppointments] Loading paid locations for customerID:', customerID);

        // siteID = the signed-in user's own site. The API proves they belong to it and only answers when that site belongs to
        // this customerID (request-auth 2026-10-20), so it must always be sent.
        const ownSiteID = siteID || (rootState && rootState.customer && rootState.customer.siteID);
        const response = await axios.post('/get-paid-locations', { customerID, siteID: ownSiteID },{ crossdomain: true });
        console.log('[onlineAppointments] loadPaidLocations response:', response.data);

        if (response.data && response.data.success && Array.isArray(response.data.locations)) {
          const locations = response.data.locations.map(loc => ({
            id: loc.siteId,
            siteId: loc.siteId,
            name: loc.name || '',
            address: loc.address || '',
            city: loc.city || '',
            state: loc.state || '',
            zip: loc.zip || ''
          }));
          console.log('[onlineAppointments] Found', locations.length, 'paid locations:', locations);
          commit('SET_PAID_LOCATIONS', locations);

          // Load status for each location (lightweight check)
          locations.forEach(loc => {
            dispatch('loadLocationStatus', { siteID: loc.siteId });
          });
        } else {
          console.log('[onlineAppointments] No paid locations found or invalid response format');
          commit('SET_PAID_LOCATIONS', []);
        }
      } catch (err) {
        console.error('[onlineAppointments] Error loading paid locations:', err);
        if (err.response) {
          console.error('[onlineAppointments] Error response data:', err.response.data);
          console.error('[onlineAppointments] Error response status:', err.response.status);
        }
        commit('SET_PAID_LOCATIONS', []);
      }
    },

    /**
     * Load just the status for a location (lightweight - doesn't load full settings)
     * Uses dedicated online-scheduling-service endpoint (follows huddle.js pattern)
     */
    async loadLocationStatus({ commit, state }, { siteID }) {
      // If we already have cached data, use it
      const cached = state.settingsCache[siteID];
      if (cached && cached.loaded) {
        const appointmentTypes = cached.settings?.appointmentTypes || [];
        const hasEmail = !!(cached.settings?.notificationEmail);
        commit('SET_LOCATION_STATUS', {
          siteId: siteID,
          status: {
            configured: appointmentTypes.length > 0 && hasEmail,
            appointmentTypeCount: appointmentTypes.length,
            hasEmail: hasEmail
          }
        });
        return;
      }

      // Otherwise, load settings via dedicated endpoint
      try {
        let response = await axios.post('/get-online-scheduling-settings', {
          siteID: siteID
        }, { crossdomain: true });

        if (response.data && response.data.success) {
          const data = response.data.data;
          const settings = data.settings || {};
          const appointmentTypes = settings.appointmentTypes || [];
          const hasEmail = !!(settings.notificationEmail);

          // Cache the data since we loaded it anyway
          commit('CACHE_LOCATION_DATA', { siteId: siteID, data });

          commit('SET_LOCATION_STATUS', {
            siteId: siteID,
            status: {
              configured: appointmentTypes.length > 0 && hasEmail,
              appointmentTypeCount: appointmentTypes.length,
              hasEmail: hasEmail
            }
          });
        }
      } catch (err) {
        console.log('Error loading location status:', err);
        commit('SET_LOCATION_STATUS', {
          siteId: siteID,
          status: { configured: false, appointmentTypeCount: 0, hasEmail: false }
        });
      }
    },

    /**
     * Load online bookings for the dashboard data table
     */
    async loadBookings({ commit }, { siteID, page = 1, pageSize = 25, search, appointmentTypeId, providerNum, dateFrom, dateTo }) {
      try {
        const payload = { siteID, page, pageSize };
        if (search) payload.search = search;
        if (appointmentTypeId) payload.appointmentTypeId = appointmentTypeId;
        if (providerNum) payload.providerNum = providerNum;
        if (dateFrom) payload.dateFrom = dateFrom;
        if (dateTo) payload.dateTo = dateTo;

        const response = await axios.post('/get-online-bookings', payload, { crossdomain: true });

        if (response.data && response.data.success) {
          return response.data;
        } else {
          throw new Error(response.data?.message || 'Failed to load bookings');
        }
      } catch (err) {
        console.error('[onlineAppointments] Error loading bookings:', err);
        throw err;
      }
    },

    /**
     * Load booking stats for KPI cards
     */
    async loadBookingStats({ commit }, { siteID }) {
      try {
        const response = await axios.post('/get-booking-stats', { siteID }, { crossdomain: true });

        if (response.data && response.data.success) {
          return response.data;
        } else {
          throw new Error(response.data?.message || 'Failed to load booking stats');
        }
      } catch (err) {
        console.error('[onlineAppointments] Error loading booking stats:', err);
        throw err;
      }
    },

    /**
     * Copy settings from one location to another
     */
    async copySettings({ commit, state, dispatch }, { fromSiteID, toSiteID, copyOptions }) {
      commit('SET_SAVING', true);
      commit('SET_ERROR', null);

      try {
        // Load source settings if not cached
        let sourceData = state.settingsCache[fromSiteID];
        if (!sourceData || !sourceData.loaded) {
          await dispatch('loadSettingsForLocation', { siteID: fromSiteID });
          sourceData = state.settingsCache[fromSiteID];
        }

        // Load target settings if not cached
        let targetData = state.settingsCache[toSiteID];
        if (!targetData || !targetData.loaded) {
          await dispatch('loadSettingsForLocation', { siteID: toSiteID });
          targetData = state.settingsCache[toSiteID];
        }

        const sourceSettings = sourceData?.settings || {};
        const targetSettings = { ...(targetData?.settings || {}) };

        // Copy based on options
        if (copyOptions.generalSettings) {
          // Copy general settings (but NOT operatory-provider mappings)
          targetSettings.notificationEmail = sourceSettings.notificationEmail;
          targetSettings.sendAppointmentNotifications = sourceSettings.sendAppointmentNotifications;
          targetSettings.acceptedInsurances = sourceSettings.acceptedInsurances ? [...sourceSettings.acceptedInsurances] : [];
          targetSettings.providerNetworkStatus = sourceSettings.providerNetworkStatus
            ? JSON.parse(JSON.stringify(sourceSettings.providerNetworkStatus))
            : {};
          targetSettings.showNetworkStatusChart = sourceSettings.showNetworkStatusChart;
          targetSettings.showNetworkStatusOnProviderSelect = sourceSettings.showNetworkStatusOnProviderSelect;
          // Scheduling window — all four keys travel together. Copying the auto-extend
          // flag without its month count (or the End Date without the flag) would leave
          // the target location with a window that cannot fire.
          targetSettings.schedulingWindowStart = sourceSettings.schedulingWindowStart || null;
          targetSettings.schedulingWindowEnd = sourceSettings.schedulingWindowEnd || null;
          targetSettings.schedulingWindowAutoExtend = sourceSettings.schedulingWindowAutoExtend === true;
          targetSettings.schedulingWindowAutoExtendMonths = sourceSettings.schedulingWindowAutoExtendMonths || null;
          // Minimum notice travels with the window. Normalized so a bad source value copies
          // as "no minimum" instead of making the target's save 400 (the server refuses it).
          targetSettings.minimumNoticeHours = normalizeMinimumNoticeHours(sourceSettings.minimumNoticeHours) || 0;
          // Note: allowPatientLocationChoice, enabledLocations, defaultLocationId are NOT copied
          // as they are customer-level settings, not location-specific
        }

        if (copyOptions.appointmentTypes) {
          // Copy appointment types (but clear operatory-provider mappings)
          const copiedTypes = (sourceSettings.appointmentTypes || []).map(type => ({
            ...type,
            id: `apt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // New ID
            operatoryProviders: [] // Clear mappings - must be configured per location
          }));
          targetSettings.appointmentTypes = copiedTypes;
        }

        if (copyOptions.blockoutRules) {
          targetSettings.ignoredBlockouts = sourceSettings.ignoredBlockouts
            ? [...sourceSettings.ignoredBlockouts]
            : [];
        }

        // Save to target location
        await dispatch('saveSettings', { siteID: toSiteID, settings: targetSettings });

        return { success: true };
      } catch (err) {
        console.log('Error copying settings:', err);
        commit('SET_ERROR', err.message || 'Failed to copy settings');
        throw err;
      } finally {
        commit('SET_SAVING', false);
      }
    }
  }
};
